import { Plus, RotateCcw } from "lucide-react";
import { useMemo } from "react";
import {
  InfiniteListBase,
  RecordContextProvider,
  useGetIdentity,
  useListContext,
  useTimeout,
  useTranslate,
} from "ra-core";
import { Link, matchPath, useLocation, useNavigate } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { MobileListHeader } from "../layout/MobileListHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Sponsoring } from "../types";
import { ContractTypeName } from "./ContractTypeName";
import { EndDateField } from "./EndDateStatus";
import { SponsoringCreate } from "./SponsoringCreate";
import { SponsoringEdit } from "./SponsoringEdit";
import { SponsoringQuickFilters } from "./SponsoringQuickFilters";
import { StageBadge } from "./StageBadge";
import { buildQuickFilterValues, NO_QUICK_FILTERS } from "./sponsoringFilters";
import {
  buildViewFilter,
  sponsoringViewBasePath,
  sponsoringViewCreateLabel,
  sponsoringViewKind,
  sponsoringViewSearchPlaceholder,
  sponsoringViewStoreKey,
  sponsoringViewTitle,
  type SponsoringView,
} from "./sponsoringViews";
import { defaultLeadStage } from "./stages";
import { useQuickFilterParam } from "./useQuickFilterParam";

const VIEWS: SponsoringView[] = ["sponsorings", "ball", "leads"];

/**
 * Mobile list: the same set of records as the desktop table of the same
 * overview, with a segmented control to switch between the three overviews
 * and the quick filter chips in a horizontally scrolling row. The create and
 * edit dialogs open on the same routes as on the desktop
 * (`${basePath}/create` and `${basePath}/:id`), so the list stays mounted
 * behind them; `${basePath}/:id/show` is the mobile show page.
 */
export const SponsoringListMobile = ({
  view = "sponsorings",
}: {
  view?: SponsoringView;
}) => {
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();
  const viewFilter = useMemo(
    () => buildViewFilter(view, config),
    [view, config],
  );
  const defaultFilter = useMemo(
    () =>
      view === "leads"
        ? undefined
        : buildQuickFilterValues({ config, quickFilters: NO_QUICK_FILTERS }),
    [config, view],
  );

  if (!identity) return null;

  return (
    <InfiniteListBase
      storeKey={`${sponsoringViewStoreKey[view]}.mobile`}
      disableSyncWithLocation
      perPage={25}
      filter={viewFilter}
      filterDefaultValues={defaultFilter}
      sort={{ field: "updated_at", order: "DESC" }}
      queryOptions={{
        onError: () => {
          /* Disable error notification: the content shows a retry button */
        },
      }}
    >
      <SponsoringListLayoutMobile view={view} />
    </InfiniteListBase>
  );
};

const SponsoringListLayoutMobile = ({ view }: { view: SponsoringView }) => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const location = useLocation();
  const { error } = useListContext<Sponsoring>();
  const basePath = sponsoringViewBasePath[view];
  const matchCreate = matchPath(`${basePath}/create`, location.pathname);
  const matchEdit = matchPath(`${basePath}/:id`, location.pathname);

  // A link such as /sponsorings?filter=expired (from the dashboard) selects
  // that quick filter, then the parameter is dropped from the URL
  useQuickFilterParam(basePath);

  const createDefaultValues = useMemo(
    () => ({
      kind: sponsoringViewKind[view],
      ...(view === "leads" ? { stage: defaultLeadStage(config) } : null),
    }),
    [view, config],
  );

  return (
    <div>
      <MobileListHeader
        title={translate(sponsoringViewTitle[view])}
        searchPlaceholder={translate(sponsoringViewSearchPlaceholder[view])}
      />
      <MobileContent search>
        <SponsoringViewSwitcher view={view} />
        <Button asChild className="mb-3 w-full">
          <Link to={`${basePath}/create`}>
            <Plus />
            {translate(sponsoringViewCreateLabel[view])}
          </Link>
        </Button>
        <SponsoringQuickFilters view={view} />
        <SponsoringListContentMobile basePath={basePath} />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
      <SponsoringCreate
        open={!!matchCreate}
        basePath={basePath}
        defaultValues={createDefaultValues}
      />
      <SponsoringEdit
        open={!!matchEdit && !matchCreate}
        id={matchEdit?.params.id}
        basePath={basePath}
      />
    </div>
  );
};

/** Segmented control switching between the three sponsoring overviews */
const SponsoringViewSwitcher = ({ view }: { view: SponsoringView }) => {
  const translate = useTranslate();
  const navigate = useNavigate();

  return (
    <ToggleGroup
      type="single"
      variant="outline"
      value={view}
      onValueChange={(value) => {
        if (value) navigate(sponsoringViewBasePath[value as SponsoringView]);
      }}
      className="mb-3 grid w-full grid-cols-3"
      aria-label={translate("resources.sponsorings.views.label")}
    >
      {VIEWS.map((candidate) => (
        <ToggleGroupItem
          key={candidate}
          value={candidate}
          className="text-xs data-[state=on]:bg-brand-blue/10 data-[state=on]:text-brand-navy"
        >
          {translate(sponsoringViewTitle[candidate])}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

const SponsoringListContentMobile = ({ basePath }: { basePath: string }) => {
  const translate = useTranslate();
  const {
    data: sponsorings,
    error,
    isPending,
    refetch,
  } = useListContext<Sponsoring>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) return null;
    return (
      <>
        {[...Array(5)].map((_, index) => (
          <div key={index} className="py-3">
            <Skeleton className="w-32 h-5 mb-2" />
            <Skeleton className="w-48 h-4" />
          </div>
        ))}
      </>
    );
  }

  if (error && !sponsorings) {
    return (
      <div className="p-4 text-center">
        <div className="text-muted-foreground mb-4">
          {translate("resources.sponsorings.list.error_loading")}
        </div>
        <Button onClick={() => refetch()}>
          <RotateCcw />
          {translate("crm.common.retry")}
        </Button>
      </div>
    );
  }

  if (sponsorings.length === 0) {
    return (
      <div className="p-4 text-muted-foreground">
        {translate("resources.sponsorings.list.no_results")}
      </div>
    );
  }

  return (
    <div className="divide-y">
      {sponsorings.map((sponsoring) => (
        <RecordContextProvider key={sponsoring.id} value={sponsoring}>
          <SponsoringItemMobile sponsoring={sponsoring} basePath={basePath} />
        </RecordContextProvider>
      ))}
    </div>
  );
};

const SponsoringItemMobile = ({
  sponsoring,
  basePath,
}: {
  sponsoring: Sponsoring;
  basePath: string;
}) => (
  <Link
    to={`${basePath}/${sponsoring.id}/show`}
    className="flex flex-col gap-1 py-3 hover:bg-muted transition-colors"
  >
    <div className="flex justify-between items-start gap-2">
      <div className="min-w-0">
        <div className="font-semibold truncate">
          <ReferenceField
            source="company_id"
            reference="companies"
            link={false}
          />
        </div>
        <div className="text-sm text-muted-foreground truncate">
          <ContractTypeName />
        </div>
      </div>
      <StageBadge stage={sponsoring.stage} />
    </div>
    <div className="text-sm text-muted-foreground">
      <EndDateField record={sponsoring} />
    </div>
  </Link>
);
